import { createFileRoute, Outlet, useMatchRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { authClient, type AuthClient } from "@/lib/auth";
import { Sidebar } from "@/components/layout/Sidebar";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  LEFT_SIDEBAR_DEFAULT_WIDTH,
  LEFT_SIDEBAR_HARD_MIN_WIDTH,
  LEFT_SIDEBAR_MIN_WIDTH,
  useSessionUI,
} from "@/stores/session-ui";
import { useShortcutLabels } from "@/hooks/use-shortcut-labels";
import { useShortcutHandler } from "@/hooks/use-shortcut-handler";
import { useBackgroundPrStatusRefresher } from "@/hooks/use-background-pr-status-refresher";
import { useDeferredBackgroundActivityEnabled } from "@/hooks/use-deferred-background-activity";
import { useFaviconWaitingIndicator } from "@/hooks/use-favicon-waiting-indicator";
import { useThreadSyncBackgroundWarming } from "@/hooks/use-thread-sync-background-warming";
import {
  useLaptopNotificationHeartbeat,
  usePushSubscription,
  useThreadCompletionNotifications,
} from "@/hooks/notifications";
import { PwaInstallBanner } from "@/components/PwaInstallBanner";
import { CommandPalette } from "@/components/CommandPalette";
import { KeyboardShortcutsDialog } from "@/components/shortcuts/KeyboardShortcutsDialog";
import { ScratchPadDrawer } from "@/components/scratch-pad/ScratchPadDrawer";
import { useScratchPadStore } from "@/stores/scratch-pad";
import { useMobileLayout } from "@/hooks/use-mobile-layout";
import {
  modeUsesLaptopNotifications,
  modeUsesMobilePush,
} from "@/lib/notification-mode";
import { SidebarSimple } from "@phosphor-icons/react";
import { useUserPreferences } from "@/hooks/use-user-preferences";
import { PreferencesHydrator } from "@/components/PreferencesHydrator";
import { identifyAnalyticsUser, resetAnalyticsUser } from "@/lib/analytics";
import { useMultiplexThreadActivity } from "@/hooks/use-multiplex-thread-activity";
import { useSessionSurfaceQueryCacheBridge } from "@/hooks/use-session-surface-query-cache-bridge";
import { usePersistedSessionSurfaceQueryCache } from "@/hooks/use-persisted-session-surface-query-cache";
import { useThreadRuntimeViewedStatePersistence } from "@/hooks/use-thread-runtime-viewed-state-persistence";
import { useRuntimeConversationViewedStatePersistence } from "@/hooks/use-runtime-conversation-viewed-state-persistence";
import { PersistentSessionPreviewFrameProvider } from "@/components/session/PersistentSessionPreviewFrameProvider";
import { PersistentStorageUploadProvider } from "@/components/settings/PersistentStorageUploadProvider";
import { AppPrimaryTabGate } from "@/components/AppPrimaryTabGate";
import { cleanupOrphanedSessionStorage } from "@/lib/session-localstorage-cleanup";
import { removeOrphanedPersistedSessionSurfaceQueriesForScope } from "@/lib/persisted-session-surface-query-cache";
import { fetchCompleteSessionList, fetchSessionList, getSessionListQueryKey } from "@/lib/session-list-query";

const MIN_MAIN_CONTENT_WIDTH = 480;
const MOBILE_SIDEBAR_MIN_WIDTH = 280;
const MOBILE_SIDEBAR_MAX_WIDTH = 360;
const MOBILE_SIDEBAR_VIEWPORT_RATIO = 0.82;

/**
 * If the auth/session bootstrap is still pending after twelve seconds, keep
 * waiting but reveal recovery controls. This is long enough for normal cold
 * Cloudflare/API starts while avoiding the Chrome-only “blank loading forever”
 * failure mode where a browser-local fetch/cache promise never resolves.
 */
const AUTH_LOADING_RECOVERY_HINT_DELAY_MS = 12_000;

/**
 * Workspace bootstrapping is required before org-scoped API calls are safe, but
 * Better Auth organization calls do not expose an AbortSignal here. Bound the
 * UI gate at fifteen seconds so a stuck browser/network request turns into a
 * retryable state instead of permanently hiding the app behind the logo.
 */
const WORKSPACE_BOOTSTRAP_TIMEOUT_MS = 15_000;

/**
 * Better Auth requires organization slugs to be unique. The existing onboarding
 * path used six base-36 random chars; keep that compact slug shape, skipping
 * the `0.` prefix produced by `Math.random().toString(36)`.
 */
const WORKSPACE_SLUG_RANDOM_SUFFIX_RADIX = 36;
const WORKSPACE_SLUG_RANDOM_SUFFIX_START_INDEX = 2;
const WORKSPACE_SLUG_RANDOM_SUFFIX_END_INDEX = 8;

type AuthSessionData = NonNullable<ReturnType<AuthClient["useSession"]>["data"]>;

function getAuthErrorMessage(args: { error: unknown; fallback: string }): string {
  if (!args.error || typeof args.error !== "object") {
    return args.fallback;
  }

  const message = (args.error as { message?: unknown }).message;
  if (typeof message === "string" && message.trim().length > 0) {
    return message;
  }

  const statusText = (args.error as { statusText?: unknown }).statusText;
  if (typeof statusText === "string" && statusText.trim().length > 0) {
    return statusText;
  }

  return args.fallback;
}

function withTimeout<T>(args: {
  description: string
  promise: Promise<T>
  timeoutMs: number
}): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`${args.description} timed out. Please retry or reload the app.`));
    }, args.timeoutMs);
  });

  return Promise.race([args.promise, timeoutPromise]).finally(() => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  });
}

async function ensureActiveOrganization(args: {
  organization: AuthClient["organization"]
  session: AuthSessionData
}): Promise<void> {
  if (args.session.session.activeOrganizationId) {
    return;
  }

  const { data: orgs, error: listError } = await args.organization.list();
  if (listError) {
    throw new Error(getAuthErrorMessage({
      error: listError,
      fallback: "Failed to load organizations.",
    }));
  }

  if (orgs && orgs.length > 0) {
    const { error: setActiveError } = await args.organization.setActive({
      organizationId: orgs[0].id,
    });
    if (setActiveError) {
      throw new Error(getAuthErrorMessage({
        error: setActiveError,
        fallback: "Failed to select organization.",
      }));
    }
    return;
  }

  const name = args.session.user.name || args.session.user.email;
  const suffix = Math.random()
    .toString(WORKSPACE_SLUG_RANDOM_SUFFIX_RADIX)
    .slice(WORKSPACE_SLUG_RANDOM_SUFFIX_START_INDEX, WORKSPACE_SLUG_RANDOM_SUFFIX_END_INDEX);
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const { data: newOrg, error: createError } = await args.organization.create({
    name: `${name}'s workspace`,
    slug: `${slug}-${suffix}`,
  });
  if (!newOrg) {
    throw new Error(getAuthErrorMessage({
      error: createError,
      fallback: "Failed to create organization.",
    }));
  }

  const { error: setActiveError } = await args.organization.setActive({
    organizationId: newOrg.id,
  });
  if (setActiveError) {
    throw new Error(getAuthErrorMessage({
      error: setActiveError,
      fallback: "Failed to select the new organization.",
    }));
  }
}

function collectAuthorizedSessionIds(args: {
  sessionGroups: Array<Array<{ id: string }> | undefined>
}): Set<string> {
  const sessionIds = new Set<string>();

  for (const sessionGroup of args.sessionGroups) {
    for (const session of sessionGroup ?? []) {
      if (typeof session.id !== "string" || session.id.trim().length === 0) {
        continue;
      }

      sessionIds.add(session.id);
    }
  }

  return sessionIds;
}

function buildAuthorizedSessionIdsGenerationKey(args: {
  scopeKey: string | null
  sessionIds: Set<string>
}): string | null {
  if (!args.scopeKey) {
    return null;
  }

  return `${args.scopeKey}:${[...args.sessionIds].sort().join(",")}`;
}

export const Route = createFileRoute("/_authed")({
  component: AuthedLayout,
});

function buildPrimaryTabScopeKey(args: { session: AuthSessionData }): string | null {
  const userId = args.session.user.id?.trim();
  if (!userId) return null;
  const organizationId = args.session.session.activeOrganizationId?.trim();
  return organizationId ? `${organizationId}:${userId}` : `bootstrap:${userId}`;
}

function AuthedLoadingState(props: {
  title: string
  description: string | null
  showRecoveryActions: boolean
  onRetry: () => void
  onReload: () => void
}) {
  return (
    <div className="flex h-full items-center justify-center bg-[#09090b]">
      <div
        className="flex max-w-sm flex-col items-center gap-4 px-6 text-center"
        style={{ animation: "hero-reveal 0.5s cubic-bezier(0.16, 1, 0.3, 1) both" }}
      >
        <div className="relative">
          <div className="absolute inset-0 rounded-2xl bg-white/[0.06] blur-lg" />
          <div className="relative size-12 overflow-hidden rounded-2xl ring-1 ring-white/[0.08]">
            <img src="/cord-logo.svg" alt="Cord" className="size-full" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="text-sm font-medium text-zinc-400">{props.title}</div>
          {props.description && (
            <div className="text-xs leading-5 text-zinc-600">{props.description}</div>
          )}
        </div>
        {props.showRecoveryActions && (
          <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
            <button
              type="button"
              onClick={props.onRetry}
              className="rounded-lg border border-white/10 bg-white/[0.08] px-3 py-1.5 text-xs font-medium text-zinc-200 transition hover:bg-white/[0.12]"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={props.onReload}
              className="rounded-lg border border-white/[0.06] px-3 py-1.5 text-xs font-medium text-zinc-500 transition hover:border-white/10 hover:text-zinc-300"
            >
              Reload app
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function AuthedLayout() {
  const {
    data: session,
    error: sessionError,
    isPending,
    refetch: refetchSession,
  } = authClient.useSession();
  const navigate = useNavigate();
  const [authLoadingRecoveryVisible, setAuthLoadingRecoveryVisible] = useState(false);
  const analyticsIdentityKeyRef = useRef<string | null>(null);

  const handleRetrySession = useCallback(() => {
    setAuthLoadingRecoveryVisible(false);
    void refetchSession().catch((error) => {
      console.error("Session refetch failed:", error);
    });
  }, [refetchSession]);

  const handleReloadApp = useCallback(() => {
    window.location.reload();
  }, []);

  const primaryTabScopeKey = useMemo(() => {
    return session?.user ? buildPrimaryTabScopeKey({ session }) : null;
  }, [session?.session.activeOrganizationId, session?.user?.id]);

  useEffect(() => {
    const userId = session?.user?.id ?? null;
    const organizationId = session?.session?.activeOrganizationId ?? null;
    const identityKey = userId
      ? `${userId}:${organizationId ?? "none"}:${session?.user?.email ?? ""}:${session?.user?.name ?? ""}`
      : null;

    if (!identityKey || !userId) {
      if (analyticsIdentityKeyRef.current !== null) {
        resetAnalyticsUser();
        analyticsIdentityKeyRef.current = null;
      }
      return;
    }

    if (analyticsIdentityKeyRef.current === identityKey) {
      return;
    }

    identifyAnalyticsUser({
      userId,
      email: session?.user?.email,
      name: session?.user?.name,
      organizationId,
    });
    analyticsIdentityKeyRef.current = identityKey;
  }, [session?.session?.activeOrganizationId, session?.user?.email, session?.user?.id, session?.user?.name]);

  useEffect(() => {
    if (!isPending) {
      setAuthLoadingRecoveryVisible(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      setAuthLoadingRecoveryVisible(true);
    }, AUTH_LOADING_RECOVERY_HINT_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [isPending]);

  useEffect(() => {
    if (isPending) return;
    if (session?.user) return;
    navigate({ to: "/login", search: { redirect: undefined, mode: undefined, email: undefined } });
  }, [isPending, navigate, session?.user]);

  if (isPending) {
    const sessionErrorMessage = sessionError
      ? getAuthErrorMessage({
        error: sessionError,
        fallback: "Failed to check your session.",
      })
      : null;
    const loadingDescription = sessionErrorMessage
      ?? (authLoadingRecoveryVisible
        ? "This is taking longer than expected. The browser may have a stuck auth or cache request; retrying here should avoid restarting Chrome."
        : null);
    const showRecoveryActions = Boolean(
      sessionErrorMessage || authLoadingRecoveryVisible,
    );

    return (
      <AuthedLoadingState
        title="Checking session…"
        description={loadingDescription}
        showRecoveryActions={showRecoveryActions}
        onRetry={handleRetrySession}
        onReload={handleReloadApp}
      />
    );
  }

  if (!session?.user) {
    return null;
  }

  return (
    <AppPrimaryTabGate key={primaryTabScopeKey ?? "no-primary-scope"} scopeKey={primaryTabScopeKey}>
      <AuthedPrimaryContent session={session} />
    </AppPrimaryTabGate>
  );
}

function AuthedPrimaryContent({ session }: { session: AuthSessionData }) {
  const isOnboardingRoute = useRouterState({
    select: (s) => s.location.pathname.replace(/\/+$/, "") === "/getting-started",
  });
  // AgentView is an alternative minimal UI that intentionally skips all the
  // heavy background queries the main app fans out (PR refresh, thread sync
  // warming, multiplex activity, persisted query caches, push subscriptions,
  // favicon waiting indicator, etc.). Detect it here so the main app shell stays
  // unmounted on AgentView routes while the primary-tab gate still applies.
  const isAgentViewRoute = useRouterState({
    select: (s) => {
      const path = s.location.pathname.replace(/\/+$/, "");
      return path === "/agent-view" || path.startsWith("/agent-view/");
    },
  });
  const [orgReady, setOrgReady] = useState(false);
  const [orgBootstrapError, setOrgBootstrapError] = useState<string | null>(null);
  const [orgBootstrapAttempt, setOrgBootstrapAttempt] = useState(0);

  const handleRetryWorkspaceBootstrap = useCallback(() => {
    setOrgBootstrapError(null);
    setOrgBootstrapAttempt((attempt) => attempt + 1);
  }, []);

  const handleReloadApp = useCallback(() => {
    window.location.reload();
  }, []);

  const sessionViewScopeKey = useMemo(() => {
    const userId = session.user.id?.trim();
    if (!userId) return null;
    const organizationId = session.session.activeOrganizationId?.trim() || "no-org";
    return `${organizationId}:${userId}`;
  }, [session.session.activeOrganizationId, session.user.id]);

  useEffect(() => {
    let cancelled = false;
    setOrgBootstrapError(null);

    const bootstrapSession = session;

    if (session.session.activeOrganizationId) {
      setOrgReady(true);
      return () => {
        cancelled = true;
      };
    }

    setOrgReady(false);

    async function ensureOrganization() {
      try {
        await withTimeout({
          description: "Workspace loading",
          promise: ensureActiveOrganization({
            organization: authClient.organization,
            session: bootstrapSession,
          }),
          timeoutMs: WORKSPACE_BOOTSTRAP_TIMEOUT_MS,
        });
        if (cancelled) return;
        setOrgReady(true);
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error
          ? error.message
          : "Failed to open workspace. Please retry or reload the app.";
        console.error("Workspace bootstrap failed:", error);
        setOrgBootstrapError(message);
        toast.error("Couldn't open workspace. Please retry.");
      }
    }

    ensureOrganization();

    return () => {
      cancelled = true;
    };
  }, [
    orgBootstrapAttempt,
    session.session.activeOrganizationId,
    session.user.email,
    session.user.id,
    session.user.name,
  ]);

  if (!orgReady) {
    const loadingTitle = orgBootstrapError
      ? "Couldn't open workspace"
      : "Opening workspace…";

    return (
      <AuthedLoadingState
        title={loadingTitle}
        description={orgBootstrapError}
        showRecoveryActions={Boolean(orgBootstrapError)}
        onRetry={handleRetryWorkspaceBootstrap}
        onReload={handleReloadApp}
      />
    );
  }

  // Render the getting-started page without the sidebar chrome so it feels
  // like a focused, full-screen onboarding experience.
  if (isOnboardingRoute) {
    return (
      <div className="h-full bg-zinc-950 text-zinc-100">
        <PreferencesHydrator />
        <Outlet />
      </div>
    );
  }

  // AgentView is intentionally minimal: skip the sidebar, command palette,
  // scratch-pad drawer, PWA install banner, persistent preview iframe host,
  // and storage upload provider. Those all spin up sockets/queries/listeners
  // that defeat the "fast and snappy" goal of the alternative UI.
  if (isAgentViewRoute) {
    return (
      <div className="h-full bg-[#09090b] text-zinc-100">
        <Outlet />
      </div>
    );
  }

  return (
    <AuthedAppShell session={session} sessionViewScopeKey={sessionViewScopeKey} />
  );
}

function AuthedAppShell({
  session,
  sessionViewScopeKey,
}: {
  session: AuthSessionData
  sessionViewScopeKey: string | null
}) {
  const matchRoute = useMatchRoute();
  const isSessionRoute = Boolean(matchRoute({ to: "/$repoId/$sessionId" }));
  const [keyboardShortcutsOpen, setKeyboardShortcutsOpen] = useState(false);
  const leftSidebarOpen = useSessionUI((s) => s.leftSidebarOpen);
  const leftSidebarWidth = useSessionUI((s) => s.leftSidebarWidth);
  const toggleLeftSidebar = useSessionUI((s) => s.toggleLeftSidebar);
  const setLeftSidebarOpen = useSessionUI((s) => s.setLeftSidebarOpen);
  const setLeftSidebarWidth = useSessionUI((s) => s.setLeftSidebarWidth);
  const newSessionCentered = useSessionUI((s) => s.newSessionCentered);
  const shortcuts = useShortcutLabels();
  const isMobileLayout = useMobileLayout();
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const orphanCleanupGenerationRef = useRef<string | null>(null);
  const { preferences } = useUserPreferences({ enabled: true });
  const notificationMode = preferences.notifications.mode;
  const laptopNotificationsEnabled = modeUsesLaptopNotifications(notificationMode);
  const mobilePushEnabled = modeUsesMobilePush(notificationMode);
  const deferredBackgroundActivityEnabled = useDeferredBackgroundActivityEnabled(true);
  const sessionSurfaceAuthEnabled = deferredBackgroundActivityEnabled && isSessionRoute;

  // Show UI toggle: any user can force-reveal the full chrome in centered mode.
  const setNewSessionCentered = useSessionUI((s) => s.setNewSessionCentered);
  const handleShowUI = useCallback(() => {
    // Immediately turn off centered mode — right sidebar and header will animate in.
    setNewSessionCentered(false);
  }, [setNewSessionCentered]);

  const { data: activeMember, isSuccess: activeMemberLoaded } = useQuery({
    queryKey: ["active-member"],
    queryFn: async () => {
      const { data } = await authClient.organization.getActiveMember();
      if (!data) {
        throw new Error("Failed to load active organization member");
      }
      return data;
    },
    enabled: sessionSurfaceAuthEnabled && Boolean(session.session.activeOrganizationId),
  });

  const shouldLoadOrganizationSessionMembership =
    activeMember?.role === "owner" || activeMember?.role === "admin";

  const { data: allSessions, isSuccess: allSessionsLoaded } = useQuery({
    queryKey: getSessionListQueryKey(),
    queryFn: () => fetchSessionList<{ id: string }>(),
    enabled: sessionSurfaceAuthEnabled,
  });

  const { data: archivedSessions, isSuccess: archivedSessionsLoaded } = useQuery({
    queryKey: getSessionListQueryKey({ archived: true, includeRuntimeConversations: false }),
    queryFn: () => fetchCompleteSessionList<{ id: string }>({ archived: true, includeRuntimeConversations: false }),
    enabled: sessionSurfaceAuthEnabled,
  });

  const { data: organizationSessions, isSuccess: organizationSessionsLoaded } = useQuery({
    queryKey: getSessionListQueryKey({ orgMembers: true }),
    queryFn: async () => {
      const sessions = await fetchSessionList<{ id: string }>({ orgMembers: true });
      return sessions.map((session) => ({ ...session, organizationThread: true }));
    },
    enabled: sessionSurfaceAuthEnabled && shouldLoadOrganizationSessionMembership,
  });

  const { data: archivedOrganizationSessions, isSuccess: archivedOrganizationSessionsLoaded } = useQuery({
    queryKey: getSessionListQueryKey({ orgMembers: true, archived: true, includeRuntimeConversations: false }),
    queryFn: async () => {
      const sessions = await fetchCompleteSessionList<{ id: string }>({ orgMembers: true, archived: true, includeRuntimeConversations: false });
      return sessions.map((session) => ({ ...session, organizationThread: true }));
    },
    enabled: sessionSurfaceAuthEnabled && shouldLoadOrganizationSessionMembership,
  });

  const sessionSurfaceAuthorizationReady =
    !sessionSurfaceAuthEnabled
      ? false
      : allSessionsLoaded
      && archivedSessionsLoaded
      && activeMemberLoaded
      && (
        !shouldLoadOrganizationSessionMembership
        || (organizationSessionsLoaded && archivedOrganizationSessionsLoaded)
      );

  const authorizedSessionIds = useMemo(() => {
    if (!sessionSurfaceAuthorizationReady) {
      return null;
    }

    return collectAuthorizedSessionIds({
      sessionGroups: [
        allSessions,
        archivedSessions,
        shouldLoadOrganizationSessionMembership ? organizationSessions : undefined,
        shouldLoadOrganizationSessionMembership ? archivedOrganizationSessions : undefined,
      ],
    });
  }, [
    allSessions,
    archivedOrganizationSessions,
    archivedSessions,
    organizationSessions,
    sessionSurfaceAuthorizationReady,
    shouldLoadOrganizationSessionMembership,
  ]);

  const authorizedSessionIdsGenerationKey = useMemo(() => {
    return authorizedSessionIds
      ? buildAuthorizedSessionIdsGenerationKey({
          scopeKey: sessionViewScopeKey,
          sessionIds: authorizedSessionIds,
        })
      : null;
  }, [authorizedSessionIds, sessionViewScopeKey]);

  useBackgroundPrStatusRefresher(deferredBackgroundActivityEnabled);
  useThreadSyncBackgroundWarming({
    enabled: deferredBackgroundActivityEnabled,
    scopeKey: sessionViewScopeKey,
  });
  useMultiplexThreadActivity({
    enabled: deferredBackgroundActivityEnabled,
    scopeKey: sessionViewScopeKey,
  });
  useThreadRuntimeViewedStatePersistence(sessionViewScopeKey);
  useRuntimeConversationViewedStatePersistence(sessionViewScopeKey);
  useSessionSurfaceQueryCacheBridge(true);
  usePersistedSessionSurfaceQueryCache({
    enabled: sessionSurfaceAuthEnabled,
    scopeKey: sessionViewScopeKey,
    authorizedSessionIds,
    authorizationReady: sessionSurfaceAuthorizationReady,
  });
  useThreadCompletionNotifications(
    deferredBackgroundActivityEnabled && laptopNotificationsEnabled,
    sessionViewScopeKey,
    laptopNotificationsEnabled,
  );
  useLaptopNotificationHeartbeat(deferredBackgroundActivityEnabled, notificationMode);
  usePushSubscription(
    deferredBackgroundActivityEnabled && mobilePushEnabled,
    mobilePushEnabled,
  );
  useFaviconWaitingIndicator({ enabled: deferredBackgroundActivityEnabled });

  useEffect(() => {
    if (!sessionSurfaceAuthEnabled || !sessionViewScopeKey || !sessionSurfaceAuthorizationReady || !authorizedSessionIds || !authorizedSessionIdsGenerationKey) {
      return;
    }

    if (orphanCleanupGenerationRef.current === authorizedSessionIdsGenerationKey) {
      return;
    }

    orphanCleanupGenerationRef.current = authorizedSessionIdsGenerationKey;
    cleanupOrphanedSessionStorage(authorizedSessionIds);
    void removeOrphanedPersistedSessionSurfaceQueriesForScope({
      scopeKey: sessionViewScopeKey,
      knownSessionIds: authorizedSessionIds,
    });
  }, [
    authorizedSessionIds,
    authorizedSessionIdsGenerationKey,
    sessionSurfaceAuthEnabled,
    sessionSurfaceAuthorizationReady,
    sessionViewScopeKey,
  ]);

  const getMaxLeftSidebarWidth = useCallback(() => {
    const layoutWidth = layoutRef.current?.clientWidth ?? window.innerWidth;
    return layoutWidth - MIN_MAIN_CONTENT_WIDTH;
  }, []);

  const resetLeftSidebarWidth = useCallback(() => {
    const maxWidth = getMaxLeftSidebarWidth();
    if (maxWidth < LEFT_SIDEBAR_HARD_MIN_WIDTH) {
      setLeftSidebarOpen(false);
      return;
    }

    const effectiveMin = Math.min(LEFT_SIDEBAR_MIN_WIDTH, maxWidth);
    setLeftSidebarWidth(
      Math.max(effectiveMin, Math.min(LEFT_SIDEBAR_DEFAULT_WIDTH, maxWidth)),
    );
  }, [getMaxLeftSidebarWidth, setLeftSidebarOpen, setLeftSidebarWidth]);

  const handleLeftResizeStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (isMobileLayout) return;

      e.preventDefault();
      resizeCleanupRef.current?.();
      const handleEl = e.currentTarget;
      const pointerId = e.pointerId;

      try {
        handleEl.setPointerCapture(pointerId);
      } catch {
        // Pointer capture can fail on unsupported browsers/input states.
      }

      const updateWidth = (clientX: number) => {
        const layoutRect = layoutRef.current?.getBoundingClientRect();
        if (!layoutRect) return;

        const nextWidth = clientX - layoutRect.left;
        const maxWidth = getMaxLeftSidebarWidth();
        if (maxWidth < LEFT_SIDEBAR_HARD_MIN_WIDTH) return;

        const effectiveMin = Math.min(LEFT_SIDEBAR_MIN_WIDTH, maxWidth);
        setLeftSidebarWidth(
          Math.max(effectiveMin, Math.min(maxWidth, nextWidth)),
        );
      };

      updateWidth(e.clientX);

      const handlePointerMove = (event: PointerEvent) => {
        updateWidth(event.clientX);
      };

      const cleanup = () => {
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", handlePointerUp);
        document.removeEventListener("pointercancel", handlePointerUp);
        window.removeEventListener("blur", handlePointerUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        if (handleEl.hasPointerCapture(pointerId)) {
          handleEl.releasePointerCapture(pointerId);
        }
        resizeCleanupRef.current = null;
      };

      const handlePointerUp = () => {
        cleanup();
      };

      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
      document.addEventListener("pointercancel", handlePointerUp);
      window.addEventListener("blur", handlePointerUp);
      resizeCleanupRef.current = cleanup;
    },
    [getMaxLeftSidebarWidth, isMobileLayout, setLeftSidebarWidth],
  );

  const toggleScratchPad = useScratchPadStore((s) => s.toggle);

  useShortcutHandler({
    id: "keyboardShortcuts",
    enabled: true,
    onShortcut: () => setKeyboardShortcutsOpen(true),
  });

  useShortcutHandler({
    id: "leftSidebar",
    enabled: true,
    onShortcut: toggleLeftSidebar,
  });

  useShortcutHandler({
    id: "scratchPad",
    enabled: true,
    onShortcut: toggleScratchPad,
  });

  useEffect(() => {
    const clampWidth = () => {
      if (isMobileLayout) return;
      const maxWidth = getMaxLeftSidebarWidth();

      if (leftSidebarOpen && maxWidth < LEFT_SIDEBAR_HARD_MIN_WIDTH) {
        setLeftSidebarOpen(false);
        return;
      }

      if (maxWidth < LEFT_SIDEBAR_HARD_MIN_WIDTH) return;

      const effectiveMin = Math.min(LEFT_SIDEBAR_MIN_WIDTH, maxWidth);
      const clamped = Math.max(
        effectiveMin,
        Math.min(maxWidth, leftSidebarWidth),
      );
      if (Math.abs(clamped - leftSidebarWidth) >= 1) {
        setLeftSidebarWidth(clamped);
      }
    };

    clampWidth();
    window.addEventListener("resize", clampWidth);
    return () => window.removeEventListener("resize", clampWidth);
  }, [
    getMaxLeftSidebarWidth,
    isMobileLayout,
    leftSidebarOpen,
    leftSidebarWidth,
    setLeftSidebarOpen,
    setLeftSidebarWidth,
  ]);

  const wasMobileLayoutRef = useRef(isMobileLayout);
  const mobileDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (isMobileLayout && !mobileDefaultAppliedRef.current && leftSidebarOpen) {
      mobileDefaultAppliedRef.current = true;
      setLeftSidebarOpen(false);
      return;
    }

    if (isMobileLayout && !wasMobileLayoutRef.current) {
      setLeftSidebarOpen(false);
    }

    if (!isMobileLayout) {
      mobileDefaultAppliedRef.current = false;
    }

    wasMobileLayoutRef.current = isMobileLayout;
  }, [isMobileLayout, leftSidebarOpen, setLeftSidebarOpen]);

  useEffect(() => {
    return () => {
      resizeCleanupRef.current?.();
    };
  }, []);

  const mobileSidebarWidth = typeof window === "undefined"
    ? MOBILE_SIDEBAR_MIN_WIDTH
    : (() => {
      const maxByViewport = Math.max(240, window.innerWidth - 12);
      const preferredWidth = Math.max(
        MOBILE_SIDEBAR_MIN_WIDTH,
        Math.floor(window.innerWidth * MOBILE_SIDEBAR_VIEWPORT_RATIO),
      );
      const cappedWidth = Math.min(MOBILE_SIDEBAR_MAX_WIDTH, maxByViewport);
      return Math.min(preferredWidth, cappedWidth);
    })();

  return (
    <PersistentStorageUploadProvider>
      <div
        ref={layoutRef}
        className="relative flex h-full overflow-hidden bg-[#09090b] text-zinc-100"
      >
        <PreferencesHydrator />
        {leftSidebarOpen && !isMobileLayout && (
          <>
            <div>
              <Sidebar width={leftSidebarWidth} deferredDataEnabled={deferredBackgroundActivityEnabled} />
            </div>
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize left sidebar"
              onPointerDown={handleLeftResizeStart}
              onDoubleClick={resetLeftSidebarWidth}
              className="group relative w-1 shrink-0 cursor-col-resize touch-none bg-transparent transition-colors hover:bg-white/[0.04]"
            >
              <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-white/20 opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
          </>
        )}
        {leftSidebarOpen && isMobileLayout && (
          <>
            <button
              type="button"
              onClick={() => setLeftSidebarOpen(false)}
              className="absolute inset-0 z-40 bg-black/70 backdrop-blur-sm"
              aria-label="Close sidebar"
            />
            <div className="absolute inset-y-0 left-0 z-50 max-w-full animate-[slide-in-left_0.14s_ease-out]">
              <Sidebar width={mobileSidebarWidth} deferredDataEnabled={deferredBackgroundActivityEnabled} />
            </div>
          </>
        )}
        <main className="relative min-w-0 flex-1 flex flex-col overflow-hidden">
          {!leftSidebarOpen && !isMobileLayout && (
            <div className="absolute left-3 top-1/2 z-40 -translate-y-1/2">
              <Tooltip
                content={
                  <span>
                    Toggle sidebar
                    <kbd className="ml-1 rounded bg-zinc-700 px-1 py-0.5 text-xs font-mono text-zinc-300">
                      {shortcuts.leftSidebar}
                    </kbd>
                  </span>
                }
              >
                <button
                  onClick={toggleLeftSidebar}
                  aria-label={`Toggle sidebar (${shortcuts.leftSidebar})`}
                  className="flex size-8 items-center justify-center rounded-md border border-white/[0.06] bg-white/[0.02] text-zinc-500 transition-all duration-200 hover:border-white/[0.1] hover:bg-white/[0.05] hover:text-zinc-300"
                >
                  <SidebarSimple className="size-4" />
                </button>
              </Tooltip>
            </div>
          )}
          {/* No standalone mobile hamburger bar — mobile pages integrate the
              menu button into their own header to avoid wasting a full row. */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {/* Keep the preview host provider above session routes so warmed
                iframes survive thread-route remounts and cross-thread switches,
                but still tear it down when leaving session pages entirely. */}
            {isSessionRoute
              ? (
                  <PersistentSessionPreviewFrameProvider>
                    <Outlet />
                  </PersistentSessionPreviewFrameProvider>
                )
              : <Outlet />}
          </div>
        </main>
        {/* Show UI toggle — available to all users in centered (new session) mode */}
        {newSessionCentered && (
          <div className="absolute right-4 top-3 z-50 animate-[fade-in_0.3s_ease-out]">
            <button
              type="button"
              onClick={handleShowUI}
              className="group flex items-center gap-2.5 rounded-lg border border-zinc-700/40 bg-zinc-900/80 px-3.5 py-2 text-[13px] font-medium text-zinc-400 shadow-lg shadow-black/20 backdrop-blur-md transition-all duration-200 hover:border-zinc-500/60 hover:bg-zinc-800/90 hover:text-zinc-200 hover:shadow-xl hover:shadow-black/30"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4 text-zinc-500 transition-colors group-hover:text-zinc-300"
              >
                <path d="M2 4.25A2.25 2.25 0 0 1 4.25 2h11.5A2.25 2.25 0 0 1 18 4.25v8.5A2.25 2.25 0 0 1 15.75 15h-3.105a3.501 3.501 0 0 0 1.1 1.677A.75.75 0 0 1 13.26 18H6.74a.75.75 0 0 1-.484-1.323A3.501 3.501 0 0 0 7.355 15H4.25A2.25 2.25 0 0 1 2 12.75v-8.5Zm1.5 0a.75.75 0 0 1 .75-.75h11.5a.75.75 0 0 1 .75.75v7.5a.75.75 0 0 1-.75.75H4.25a.75.75 0 0 1-.75-.75v-7.5Z" />
              </svg>
              <span>Show UI</span>
            </button>
          </div>
        )}
        <PwaInstallBanner />
        <CommandPalette onOpenKeyboardShortcuts={() => setKeyboardShortcutsOpen(true)} />
        <KeyboardShortcutsDialog
          open={keyboardShortcutsOpen}
          onOpenChange={setKeyboardShortcutsOpen}
        />
        <ScratchPadDrawer />
      </div>
    </PersistentStorageUploadProvider>
  );
}
